import React, { useEffect, useRef } from 'react';

// Accessible modal wrapper (audit A1). Adds what the hand-rolled overlays all
// lacked: role="dialog" + aria-modal, focus the first control on open, trap Tab
// within the dialog, Esc → onClose, and restore focus to the opener on close.
// Backdrop click → onClose; clicks inside the sheet are contained.
//
// Callers pass their existing `backdropStyle` / `sheetStyle` so each modal keeps
// its own look (centered card vs bottom sheet) — this only layers on the a11y
// behavior, no visual change.
export default function Modal({
  onClose,
  children,
  backdropStyle,
  sheetStyle,
  ariaLabel,
  labelledBy,
}) {
  const sheetRef = useRef(null);
  const restoreRef = useRef(null);

  // Keep the latest onClose in a ref so the focus/trap effect below can run ONCE
  // per open instead of re-running whenever onClose's identity changes. Callers
  // pass a fresh inline handler every render (e.g. SaveAccountModal's `close`),
  // and on a keystroke-driven re-render that used to re-fire this effect — whose
  // cleanup restores focus to the opener and whose body re-focuses the first
  // field — yanking the caret out of whatever input the user was typing in (the
  // "typed, then got sent back to re-type" bug). The effect now has [] deps.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    restoreRef.current =
      typeof document !== 'undefined' ? document.activeElement : null;
    const sheet = sheetRef.current;
    const focusable = () =>
      sheet
        ? Array.from(
            sheet.querySelectorAll(
              'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
            )
          ).filter((el) => el.offsetParent !== null)
        : [];

    // Focus the first focusable control (fall back to the dialog itself).
    (focusable()[0] || sheet)?.focus?.();

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onCloseRef.current?.();
        return;
      }
      if (e.key !== 'Tab') return;
      const f = focusable();
      if (f.length === 0) {
        e.preventDefault();
        return;
      }
      const first = f[0];
      const last = f[f.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && (active === first || active === sheet)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      restoreRef.current?.focus?.();
    };
    // Run once per open: set up focus + trap on mount, tear down (restoring focus
    // to the opener) on unmount. onClose is read via onCloseRef so its changing
    // identity never re-fires this. eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={backdropStyle} onClick={onClose}>
      <div
        ref={sheetRef}
        role="dialog"
        aria-modal="true"
        aria-label={labelledBy ? undefined : ariaLabel}
        aria-labelledby={labelledBy}
        tabIndex={-1}
        style={{ ...sheetStyle, outline: 'none' }}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}
